import { FF_ENABLE_ERX_STATUS_DISPLAY } from '@/constants/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { capitalizeFirstLetter } from '@montugroup/circuit-shared';
import { Box, Typography } from '@montugroup/design-system';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  AvailableInstructions,
  AvailableProductStatusChip,
  OutOfStockInstructions,
  OutOfStockStatusChip,
  ReviewNewScriptCardRoot,
} from './ReviewNewScriptCard.styles';
import type { ReviewNewScriptInterface } from './types';
import { UnavailableNewScriptCard } from './UnavailableNewScriptCard';

export function ReviewNewScriptCard(props: ReviewNewScriptInterface) {
  const { token, productMatchValidationStatus, productStockStatus, isError, erxStatus } = props;

  const isDisplayErxStatusEnabled = Boolean(useFeatureFlag(FF_ENABLE_ERX_STATUS_DISPLAY));
  const shouldDisplayErxStatus = isDisplayErxStatusEnabled && erxStatus;
  const formattedErxStatus = capitalizeFirstLetter(erxStatus ?? '');

  if (isError || productMatchValidationStatus === 'REJECTED') {
    return (
      <UnavailableNewScriptCard
        productName={token}
        shouldShowStatusChip={!isDisplayErxStatusEnabled}
        issue={{
          title: shouldDisplayErxStatus ? formattedErxStatus : 'Unavailable',
          info: ['Your prescription is ineligible.'],
          isHighSeverity: true,
          requiresInfoOutline: true,
        }}
      />
    );
  }

  if (productMatchValidationStatus === 'AWAITING VALIDATION') {
    return (
      <UnavailableNewScriptCard
        productName={token}
        issue={{
          title: shouldDisplayErxStatus ? formattedErxStatus : 'Script in review',
          info: [
            'We’re verifying your script to make sure it matches the product in our catalogue. Please check again later.',
          ],
          isHighSeverity: false,
        }}
      />
    );
  }

  if (productStockStatus === 'OUT_OF_STOCK') {
    return (
      <ReviewNewScriptCardRoot
        aria-label={`Review Card for Out Of Stock Product: ${token}`}
        data-testid="review-new-script-card-oos"
      >
        <Box>
          <OutOfStockStatusChip
            variant="outlined"
            label={shouldDisplayErxStatus ? formattedErxStatus : 'Out Of Stock'}
          />
        </Box>
        <Typography variant="subtitle2" fontWeight="semibold">
          {token}
        </Typography>

        <OutOfStockInstructions>
          <InfoOutlined />
          <Box sx={{ display: 'block' }}>
            <Typography
              sx={{
                '&:not(:first-of-type)': {
                  mt: 4,
                },
              }}
            >
              This medication is currently out of stock. Please consult your doctor to discuss any alternatives.
            </Typography>
          </Box>
        </OutOfStockInstructions>
      </ReviewNewScriptCardRoot>
    );
  }

  return (
    <ReviewNewScriptCardRoot
      aria-label={`Review Card for Available Product: ${token}`}
      data-testid="review-new-script-card-available"
    >
      <Box>
        <AvailableProductStatusChip
          variant="outlined"
          label={shouldDisplayErxStatus ? formattedErxStatus : 'Available'}
        />
      </Box>
      <Typography variant="subtitle2" fontWeight="semibold">
        {token}
      </Typography>

      <AvailableInstructions>
        <InfoOutlined />
        <Box sx={{ display: 'block' }}>
          <Typography
            sx={{
              '&:not(:first-of-type)': {
                mt: 4,
              },
            }}
          >
            We have added this medication to your prescriptions. You can order it now.
          </Typography>
        </Box>
      </AvailableInstructions>
    </ReviewNewScriptCardRoot>
  );
}

export default ReviewNewScriptCard;
